'use client'

import { useState, useEffect, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { Instrument_Serif } from 'next/font/google'
import { requestNewInviteLink, requestPasswordReset } from './actions'
import { MK } from '../marketing-tokens'

const instrumentSerif = Instrument_Serif({ subsets: ['latin'], weight: '400', style: ['normal', 'italic'], display: 'swap', variable: '--font-serif' })

// ── Design tokens — sourced from the shared marketing palette ─────────────
// (login's "greenDeep" is the marketing primary green; "greenMid" its hover)
const C = {
  greenDeep:  MK.green,
  greenMid:   MK.greenDeep,
  cream:      MK.bg,
  creamWarm:  '#F2F0EA',   // login-only warm panel shade (no MK equivalent)
  ink:        MK.ink,
  inkMuted:   MK.body,
  inkFaint:   MK.faint,
  line:       MK.line,
  error:      MK.error,
  errorBg:    'rgba(192,57,43,0.07)',
  errorBorder:'rgba(192,57,43,0.3)',
}

// ── Inner component — uses useSearchParams() which requires Suspense ──────
function LoginForm() {
  const [email,     setEmail]     = useState('')
  const [password,  setPassword]  = useState('')
  const [error,     setError]     = useState('')
  const [loading,      setLoading]      = useState(false)
  const [rememberMe,   setRememberMe]   = useState(true)
  const [logoUrl,      setLogoUrl]      = useState<string | null>(null)
  const [brandName,    setBrandName]    = useState<string>('OBI-HUB')

  // ── Forgot-password state ────────────────────────────────────────────────
  const [showForgot,       setShowForgot]       = useState(false)
  const [forgotEmail,      setForgotEmail]      = useState('')
  const [forgotLoading,    setForgotLoading]    = useState(false)
  const [forgotError,      setForgotError]      = useState('')
  const [forgotSent,       setForgotSent]       = useState(false)

  // ── Request new invite state ─────────────────────────────────────────────
  const [showInviteRequest,    setShowInviteRequest]    = useState(false)
  const [inviteEmail,          setInviteEmail]          = useState('')
  const [inviteLoading,        setInviteLoading]        = useState(false)
  const [inviteError,          setInviteError]          = useState('')
  const [inviteSent,           setInviteSent]           = useState(false)

  const router       = useRouter()
  const searchParams = useSearchParams()
  const supabase     = createClient()

  useEffect(() => {
    // Show error passed via query string from /auth/callback failures
    const qErr  = searchParams.get('error')
    const qType = searchParams.get('type')

    if (qErr === 'auth_callback_failed') {
      if (qType === 'invite') {
        // Auto-open the invite request panel for expired invite links
        setShowInviteRequest(true)
      } else {
        // Auto-open the forgot-password panel — reset links are one-time, so
        // the fix is always to request a fresh one right here.
        setShowForgot(true)
        setForgotError('That link is no longer valid — it may have expired or already been used. Request a fresh one below.')
      }
    }

    // Resolve brand from subdomain slug
    const resolveLoginBrand = async () => {
      const slugFromQuery    = searchParams.get('brand')
      const hostname         = window.location.hostname
      const parts            = hostname.split('.')
      const slugFromSubdomain = parts.length >= 3 ? parts[0] : null
      const slug             = slugFromQuery ?? slugFromSubdomain

      if (slug && slug !== 'app' && slug !== 'www' && slug !== 'localhost') {
        const { data } = await supabase
          .from('brands')
          .select('name, logo_url')
          .eq('slug', slug)
          .eq('active', true)
          .maybeSingle()

        if (data) {
          if (data.logo_url) setLogoUrl(data.logo_url)
          if (data.name)     setBrandName(data.name)
          return
        }
      }

      // Fallback: first active brand
      const { data: firstBrand } = await supabase
        .from('brands')
        .select('name, logo_url')
        .eq('active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (firstBrand) {
        if (firstBrand.logo_url) setLogoUrl(firstBrand.logo_url)
        if (firstBrand.name)     setBrandName(firstBrand.name)
      }
    }

    resolveLoginBrand()

    // Handle Supabase implicit-flow hash params (password reset / invite links)
    const hash = window.location.hash
    if (!hash) return

    const params    = new URLSearchParams(hash.slice(1))
    const errorCode = params.get('error_code')
    const errorDesc = params.get('error_description')

    if (errorCode) {
      const msg = errorDesc
        ? decodeURIComponent(errorDesc.replace(/\+/g, ' '))
        : 'The link has expired or is invalid.'
      setError(
        errorCode === 'otp_expired'
          ? 'This link has expired. Please use the form below to request a new password reset.'
          : msg
      )
      window.history.replaceState(null, '', window.location.pathname)
      return
    }

    const accessToken  = params.get('access_token')
    const refreshToken = params.get('refresh_token')
    const type         = params.get('type')

    if (accessToken && refreshToken && (type === 'recovery' || type === 'invite')) {
      supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: sessionError }) => {
          if (!sessionError) {
            router.push('/set-password')
          } else {
            setError('Could not restore session. Please request a new password reset below.')
          }
        })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })

    if (signInError) {
      setError(
        /not confirmed/i.test(signInError.message)
          ? 'Your email hasn’t been verified yet. Check your inbox for the confirmation link we sent when you signed up.'
          : 'Invalid email or password. Please try again.'
      )
      setLoading(false)
    } else {
      // Supabase by default persists the refresh token in localStorage so the
      // user stays logged in across browser restarts (for weeks).
      // If "Keep me signed in" is unchecked, we store a flag in sessionStorage
      // so the app layout can sign the user out when the tab/window closes.
      if (!rememberMe) {
        sessionStorage.setItem('obi-session-only', '1')
      } else {
        sessionStorage.removeItem('obi-session-only')
      }
      // Full-page navigation to the post-login hub — NOT router.push().
      // A soft push('/home') triggers the (app) layout's server redirect to
      // /onboarding mid-navigation while the paired router.refresh() re-fetches
      // /login (which the proxy 307s to /redirect for authed users); the racing
      // RSC responses can commit an empty tree — a blank page until manual
      // reload. A document navigation keeps every redirect plain HTTP.
      window.location.assign('/redirect')
    }
  }

  // ── Forgot password handler ──────────────────────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setForgotLoading(true)
    setForgotError('')

    // Server action (generateLink + Resend) — the emailed link works in any
    // browser, unlike resetPasswordForEmail's PKCE link which only works in
    // the browser that requested it. Origin keeps the link on this subdomain.
    const result = await requestPasswordReset(forgotEmail.trim(), window.location.origin)

    setForgotLoading(false)

    if ('error' in result && result.error) {
      setForgotError(result.error)
    } else {
      setForgotSent(true)
    }
  }

  // ── Request new invite handler ───────────────────────────────────────────
  const handleInviteRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteLoading(true)
    setInviteError('')

    const result = await requestNewInviteLink(inviteEmail.trim())

    setInviteLoading(false)

    if ('error' in result && result.error) {
      setInviteError(result.error)
    } else {
      setInviteSent(true)
    }
  }

  // ── Focus / blur handlers for input fields ───────────────────────────────
  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor     = C.greenDeep
    e.target.style.boxShadow       = `0 0 0 3px rgba(46,85,69,0.14)`
    e.target.style.backgroundColor = '#FFFFFF'
  }
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor     = '#DEDCD2'
    e.target.style.boxShadow       = 'none'
    e.target.style.backgroundColor = C.cream
  }

  const inputBaseStyle: React.CSSProperties = {
    width:           '100%',
    padding:         '13px 16px',
    backgroundColor: C.cream,
    border:          '1px solid #DEDCD2',
    borderRadius:    '8px',
    fontSize:        '14px',
    color:           C.ink,
    fontFamily:      'var(--font-geist-sans), system-ui, sans-serif',
    outline:         'none',
    transition:      'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
    WebkitAppearance: 'none',
    appearance:      'none',
    boxSizing:       'border-box',
  }

  const labelStyle: React.CSSProperties = {
    display:       'block',
    fontSize:      '11.5px',
    fontWeight:    500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color:         C.inkMuted,
    marginBottom:  '7px',
  }

  return (
    <div
      className={`${instrumentSerif.variable} mobile-no-zoom`}
      style={{
        fontFamily:      'var(--font-geist-sans), system-ui, sans-serif',
        backgroundColor: C.creamWarm,
        minHeight:       '100vh',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '24px',
      }}
    >

      <div style={{ width: '100%', maxWidth: '480px' }}>
        {/* Card */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius:    '16px',
            padding:         'clamp(40px, 5vw, 56px) clamp(28px, 5vw, 52px)',
            boxShadow:       '0 12px 40px -12px rgba(27,51,42,0.18)',
            border:          '1px solid #E7E5DC',
          }}
        >
          {/* ── Logo ── */}
          <div style={{ marginBottom: '32px' }}>
            {logoUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={logoUrl}
                alt={`${brandName} logo`}
                style={{ maxWidth: '180px', maxHeight: '56px', objectFit: 'contain' }}
              />
            ) : (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src="/logo-text-green.png"
                alt="OBI-Hub"
                style={{ height: '28px', width: 'auto' }}
              />
            )}
          </div>

          {/* ── Divider ── */}
          <div style={{ width: '100%', height: 0, borderTop: '1px dashed #E7E5DC', marginBottom: '36px' }} />

          {/* ════════════════════════════════════════════════════════════════
              SIGN-IN FORM (default view)
          ════════════════════════════════════════════════════════════════ */}
          {!showForgot && !showInviteRequest && (
            <>
              {/* ── Tagline ── */}
              <p
                style={{
                  fontFamily:    "'Instrument Serif', serif",
                  fontSize:      'clamp(24px, 4vw, 28px)',
                  color:         C.ink,
                  lineHeight:    1.2,
                  marginBottom:  '32px',
                  letterSpacing: '-0.02em',
                }}
              >
                Welcome back.
              </p>

              {/* ── Form ── */}
              <form onSubmit={handleSignIn}>
                {/* Email */}
                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="email" style={labelStyle}>Email address</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@yourbrand.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    style={inputBaseStyle}
                  />
                </div>

                {/* Password */}
                <div style={{ marginBottom: '0' }}>
                  <label htmlFor="password" style={labelStyle}>Password</label>
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    placeholder="••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    style={inputBaseStyle}
                  />
                </div>

                {/* Remember me */}
                <label
                  style={{
                    display:     'flex',
                    alignItems:  'center',
                    gap:         '9px',
                    marginTop:   '16px',
                    cursor:      'pointer',
                    userSelect:  'none',
                  }}
                >
                  {/* Real checkbox, visually replaced by the styled box below */}
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(v => !v)}
                    style={{
                      position: 'absolute', width: 1, height: 1,
                      opacity: 0, pointerEvents: 'none',
                    }}
                  />
                  <div
                    aria-hidden="true"
                    style={{
                      width:           '17px',
                      height:          '17px',
                      borderRadius:    '5px',
                      border:          `1.5px solid ${rememberMe ? C.greenDeep : '#CCCAC0'}`,
                      backgroundColor: rememberMe ? C.greenDeep : 'transparent',
                      display:         'flex',
                      alignItems:      'center',
                      justifyContent:  'center',
                      flexShrink:      0,
                      transition:      'background 0.15s ease, border-color 0.15s ease',
                    }}
                  >
                    {rememberMe && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: '13px', color: C.inkMuted, fontFamily: 'var(--font-geist-sans), system-ui, sans-serif' }}>
                    Keep me signed in
                  </span>
                </label>

                {/* Error message */}
                {error && (
                  <div
                    style={{
                      marginTop:       '16px',
                      padding:         '12px 16px',
                      borderRadius:    '10px',
                      backgroundColor: C.errorBg,
                      border:          `1px solid ${C.errorBorder}`,
                      color:           C.error,
                      fontSize:        '13px',
                      lineHeight:      1.5,
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = C.greenMid
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = C.greenDeep
                  }}
                  style={{
                    width:           '100%',
                    padding:         '14px',
                    backgroundColor: loading ? C.greenMid : C.greenDeep,
                    border:          'none',
                    borderRadius:    '10px',
                    cursor:          loading ? 'not-allowed' : 'pointer',
                    color:           '#FFFFFF',
                    fontFamily:      'var(--font-geist-sans), system-ui, sans-serif',
                    fontSize:        '14px',
                    fontWeight:      500,
                    letterSpacing:   '0.02em',
                    marginTop:       '28px',
                    transition:      'background 0.2s ease, transform 0.1s ease',
                    boxShadow:       'none',
                    opacity:         loading ? 0.8 : 1,
                  }}
                >
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>

              {/* ── Footer ── */}
              <div
                style={{
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'space-between',
                  marginTop:      '20px',
                  fontSize:       '12px',
                  color:          C.inkFaint,
                }}
              >
                <span>Team access only</span>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setError(''); setForgotEmail(email) }}
                  style={{
                    background:  'none',
                    border:      'none',
                    padding:     0,
                    cursor:      'pointer',
                    color:       C.greenDeep,
                    fontSize:    '12px',
                    transition:  'color 0.15s ease',
                    fontFamily:  'var(--font-geist-sans), system-ui, sans-serif',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.greenMid)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.greenDeep)}
                >
                  Forgot password →
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════
              FORGOT PASSWORD FORM
          ════════════════════════════════════════════════════════════════ */}
          {showForgot && (
            <>
              {/* ── Heading ── */}
              <p
                style={{
                  fontFamily:    "'Instrument Serif', serif",
                  fontSize:      'clamp(22px, 4vw, 26px)',
                  color:         C.ink,
                  lineHeight:    1.2,
                  marginBottom:  '12px',
                  letterSpacing: '-0.02em',
                }}
              >
                Reset your password.
              </p>
              <p style={{ fontSize: '13px', color: C.inkMuted, marginBottom: '28px', lineHeight: 1.5 }}>
                Enter the email address on your account and we&apos;ll send you a link to choose a new password.
              </p>

              {forgotSent ? (
                /* ── Success state ── */
                <div
                  style={{
                    padding:         '20px',
                    borderRadius:    '12px',
                    backgroundColor: '#EDF4F0',
                    border:          `1px solid #CBDED5`,
                    color:           C.greenDeep,
                    fontSize:        '14px',
                    lineHeight:      1.6,
                    marginBottom:    '24px',
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Check your inbox</strong>
                  If <strong>{forgotEmail}</strong> is registered, you&apos;ll receive a password reset link shortly. Check your spam folder if it doesn&apos;t arrive within a few minutes.
                </div>
              ) : (
                /* ── Request form ── */
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="forgot-email" style={labelStyle}>Email address</label>
                    <input
                      id="forgot-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@yourbrand.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      style={inputBaseStyle}
                    />
                  </div>

                  {forgotError && (
                    <div
                      style={{
                        marginBottom:    '16px',
                        padding:         '12px 16px',
                        borderRadius:    '10px',
                        backgroundColor: C.errorBg,
                        border:          `1px solid ${C.errorBorder}`,
                        color:           C.error,
                        fontSize:        '13px',
                        lineHeight:      1.5,
                      }}
                    >
                      {forgotError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail.trim()}
                    style={{
                      width:           '100%',
                      padding:         '14px',
                      backgroundColor: forgotLoading ? C.greenMid : C.greenDeep,
                      border:          'none',
                      borderRadius:    '10px',
                      cursor:          forgotLoading || !forgotEmail.trim() ? 'not-allowed' : 'pointer',
                      color:           '#FFFFFF',
                      fontFamily:      'var(--font-geist-sans), system-ui, sans-serif',
                      fontSize:        '14px',
                      fontWeight:      500,
                      letterSpacing:   '0.02em',
                      transition:      'background 0.2s ease',
                      boxShadow:       'none',
                      opacity:         forgotLoading || !forgotEmail.trim() ? 0.7 : 1,
                    }}
                  >
                    {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
              )}

              {/* ── Back link ── */}
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotSent(false); setForgotError('') }}
                  style={{
                    background:  'none',
                    border:      'none',
                    padding:     0,
                    cursor:      'pointer',
                    color:       C.inkMuted,
                    fontSize:    '12px',
                    transition:  'color 0.15s ease',
                    fontFamily:  'var(--font-geist-sans), system-ui, sans-serif',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.ink)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.inkMuted)}
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════════
              REQUEST NEW INVITE FORM
          ════════════════════════════════════════════════════════════════ */}
          {showInviteRequest && (
            <>
              {/* ── Heading ── */}
              <p
                style={{
                  fontFamily:    "'Instrument Serif', serif",
                  fontSize:      'clamp(22px, 4vw, 26px)',
                  color:         C.ink,
                  lineHeight:    1.2,
                  marginBottom:  '12px',
                  letterSpacing: '-0.02em',
                }}
              >
                Your invite has expired.
              </p>
              <p style={{ fontSize: '13px', color: C.inkMuted, marginBottom: '28px', lineHeight: 1.5 }}>
                Enter your email and we&apos;ll notify your workspace admin to send you a fresh invite link.
              </p>

              {inviteSent ? (
                /* ── Success state ── */
                <div
                  style={{
                    padding:         '20px',
                    borderRadius:    '12px',
                    backgroundColor: '#EDF4F0',
                    border:          `1px solid #CBDED5`,
                    color:           C.greenDeep,
                    fontSize:        '14px',
                    lineHeight:      1.6,
                    marginBottom:    '24px',
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '4px' }}>Request sent</strong>
                  We&apos;ve notified your workspace admin. They&apos;ll send you a fresh invite link shortly.
                </div>
              ) : (
                /* ── Request form ── */
                <form onSubmit={handleInviteRequest}>
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="invite-email" style={labelStyle}>Email address</label>
                    <input
                      id="invite-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@yourbrand.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      style={inputBaseStyle}
                    />
                  </div>

                  {inviteError && (
                    <div
                      style={{
                        marginBottom:    '16px',
                        padding:         '12px 16px',
                        borderRadius:    '10px',
                        backgroundColor: C.errorBg,
                        border:          `1px solid ${C.errorBorder}`,
                        color:           C.error,
                        fontSize:        '13px',
                        lineHeight:      1.5,
                      }}
                    >
                      {inviteError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={inviteLoading || !inviteEmail.trim()}
                    style={{
                      width:           '100%',
                      padding:         '14px',
                      backgroundColor: inviteLoading ? C.greenMid : C.greenDeep,
                      border:          'none',
                      borderRadius:    '10px',
                      cursor:          inviteLoading || !inviteEmail.trim() ? 'not-allowed' : 'pointer',
                      color:           '#FFFFFF',
                      fontFamily:      'var(--font-geist-sans), system-ui, sans-serif',
                      fontSize:        '14px',
                      fontWeight:      500,
                      letterSpacing:   '0.02em',
                      transition:      'background 0.2s ease',
                      boxShadow:       'none',
                      opacity:         inviteLoading || !inviteEmail.trim() ? 0.7 : 1,
                    }}
                  >
                    {inviteLoading ? 'Sending…' : 'Request New Invite'}
                  </button>
                </form>
              )}

              {/* ── Back link ── */}
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setShowInviteRequest(false); setInviteSent(false); setInviteError('') }}
                  style={{
                    background:  'none',
                    border:      'none',
                    padding:     0,
                    cursor:      'pointer',
                    color:       C.inkMuted,
                    fontSize:    '12px',
                    transition:  'color 0.15s ease',
                    fontFamily:  'var(--font-geist-sans), system-ui, sans-serif',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = C.ink)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = C.inkMuted)}
                >
                  ← Back to sign in
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Suspense wrapper required by Next.js App Router for useSearchParams() ──
export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}
